import { useQuery } from "@apollo/client";
import _ from "lodash";
import { useState, useMemo, ReactElement } from "react";
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault,
  BooleanParam,
} from "use-query-params";

import {
  CreateTenantMutation,
  CreateTenantMutationVariables,
  DeactivateTenantsMutation,
  DeactivateTenantsMutationVariables,
  ListTenantsQuery,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  CREATE_TENANT,
  DEACTIVATE_TENANTS,
  LIST_TENANTS,
} from "~frontend/graphql/tenant";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import useMutation from "~frontend/util/useMutation";

import AddTenantDialog from "./AddTenantDialog";
import TenantTable from "./TenantTable";
import { TENANT_SORTS, TenantSort } from "./constants";

function validateSort(sort: string): TenantSort {
  return TENANT_SORTS.find(s => s.key === sort)?.key ?? TENANT_SORTS[0].key;
}

export default function Tenants(): ReactElement {
  useBreadcrumbs([
    {
      label: "Tenants",
      path: "/staff/tenants",
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, TENANT_SORTS[0].key)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [addTenantOpenInitially, setAddTenantOpenInitially] = useQueryParam(
    "create",
    withDefault(BooleanParam, false)
  );

  const [addTenantInitialName, setAddTenantInitialName] = useQueryParam(
    "name",
    withDefault(StringParam, "")
  );

  const [addTenantInitialSlug, setAddTenantInitialSlug] = useQueryParam(
    "slug",
    withDefault(StringParam, "")
  );

  const [addTenantInitialOrgOwnerEmail, setAddTenantInitialOrgOwnerEmail] =
    useQueryParam("owner_email", withDefault(StringParam, ""));

  const [addTenantInitialOrgOwnerName, setAddTenantInitialOrgOwnerName] =
    useQueryParam("owner_name", withDefault(StringParam, ""));

  const validatedSort = validateSort(sort);

  const { loading, error, data, refetch } =
    useQuery<ListTenantsQuery>(LIST_TENANTS);

  const [addTenantDialogOpen, toggleAddTenantDialog] = useState(
    addTenantOpenInitially
  );

  const [addTenant] = useMutation<
    CreateTenantMutation,
    CreateTenantMutationVariables
  >(CREATE_TENANT);

  const [deactivateTenant] = useMutation<
    DeactivateTenantsMutation,
    DeactivateTenantsMutationVariables
  >(DEACTIVATE_TENANTS, {
    onErrorSnackbarMessage: "Error deactivating tenant",
    refetchQueries: [LIST_TENANTS],
    awaitRefetchQueries: true,
  });

  const filteredTenants: ListTenantsQuery["listTenants"] = useMemo(() => {
    const allTenants = data?.listTenants ?? [];

    // filter
    const filteredTenants = search
      ? allTenants.filter(
          s =>
            s.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
            s.slug.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
      : allTenants.slice(0);

    // sort
    return filteredTenants.sort((a, b) => {
      switch (validatedSort) {
        case "slug_asc":
          return (a.slug || "").localeCompare(b.slug || "");
        case "slug_desc":
          return (b.slug || "").localeCompare(a.slug || "");
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [data, validatedSort, search]);

  const rows = filteredTenants.slice(page * pageSize, (page + 1) * pageSize);

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddTenant({
    name,
    slug,
    desiredPartition,
    orgOwnerEmail,
    orgOwnerName,
  }: {
    name: string;
    slug: string;
    desiredPartition: number | null;
    orgOwnerEmail: string | null;
    orgOwnerName: string | null;
  }) {
    const { errors } = await addTenant({
      variables: {
        input: { name, slug, desiredPartition, orgOwnerEmail, orgOwnerName },
      },
    });

    if (errors) {
      throw errors;
    } else {
      await refetch();
      toggleAddTenantDialog(false);

      setAddTenantOpenInitially(false);
      setAddTenantInitialName("");
      setAddTenantInitialSlug("");
      setAddTenantInitialOrgOwnerEmail("");
      setAddTenantInitialOrgOwnerName("");
    }
  }

  async function handleDeactivateTenant(tenantId: string) {
    await deactivateTenant({ variables: { input: { tenantIds: [tenantId] } } });
  }

  return (
    <PageContainer maxWidth="lg">
      <TableControls
        addButtons={[
          {
            label: "Add Tenant",
            onClick: () => toggleAddTenantDialog(true),
            id: "add-tenant",
          },
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as TenantSort),
          options: TENANT_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label,
          })),
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search for a tenant...",
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <TenantTable
            rows={rows}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={filteredTenants.length}
            onDeactivateTenant={handleDeactivateTenant}
          />
        )}
      </TableBox>
      <AddTenantDialog
        open={addTenantDialogOpen}
        onClose={() => toggleAddTenantDialog(false)}
        onSubmit={handleAddTenant}
        initialValues={{
          name: addTenantInitialName,
          slug: addTenantInitialSlug,
          orgOwnerEmail: addTenantInitialOrgOwnerEmail,
          orgOwnerName: addTenantInitialOrgOwnerName,
        }}
      />
    </PageContainer>
  );
}
